import { useIonToast } from "@ionic/react";
import { checkmarkCircle } from "ionicons/icons";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

interface QuestionnaireCompletionFeedbackContextValue {
  showCompletionFeedback(): void;
}

const QuestionnaireCompletionFeedbackContext =
  createContext<QuestionnaireCompletionFeedbackContextValue>({
    showCompletionFeedback: () =>
      console.warn("QuestionnaireCompletionFeedbackContext is not initialized"),
  });

/** Id of the app's tab bar (navigation/Tabs.tsx) — the toast sits just above it. */
const TAB_BAR_ID = "ion-tab-bar";
/** How long to wait for the destination page's tab bar to appear. */
const TAB_BAR_WAIT_MS = 1000;
const TAB_BAR_POLL_MS = 50;

/** The tab bar when it is on screen. It is position:fixed, so offsetParent is
 *  always null — check its box instead. */
const visibleTabBar = (): HTMLElement | null => {
  const bar = document.getElementById(TAB_BAR_ID);
  return bar && bar.getBoundingClientRect().height > 0 ? bar : null;
};

/**
 * "Questionnaire terminé, merci !" once a questionnaire is finished. The caller decides
 * when it is due (SequenceComponent skips it where the island's score gauge already
 * says it). An Ionic toast, so it stays up across the page change it comes with and
 * screen readers announce it. `enabled` switches it off app-wide.
 *
 * It sits just above the tab bar, never over the page's cards. The questionnaire
 * screen has no tab bar and the message is due right before leaving it, so it waits
 * (up to TAB_BAR_WAIT_MS) for the destination page's bar to appear and anchors to it.
 * If none comes — e.g. the sequence's next questionnaire opens — it shows at the top,
 * clear of the Précédent / Suivant buttons.
 */
const QuestionnaireCompletionFeedbackProvider: React.FC<{
  children: ReactNode;
  enabled?: boolean;
}> = ({ children, enabled = true }) => {
  const { t } = useTranslation();
  const [presentToast] = useIonToast();

  const showCompletionFeedback = useCallback(() => {
    if (!enabled) return;
    const startedAt = Date.now();
    const present = (anchor: HTMLElement | null): void => {
      // The card-language success toast (.success-toast in theme/common.scss).
      presentToast({
        message: t("questionnaire_completed"),
        cssClass: "success-toast",
        position: anchor ? "bottom" : "top",
        positionAnchor: anchor ?? undefined,
        icon: checkmarkCircle,
        duration: 2500,
      });
    };
    const waitForTabBar = (): void => {
      const bar = visibleTabBar();
      if (bar || Date.now() - startedAt >= TAB_BAR_WAIT_MS) {
        present(bar);
        return;
      }
      window.setTimeout(waitForTabBar, TAB_BAR_POLL_MS);
    };
    waitForTabBar();
  }, [enabled, presentToast, t]);

  const contextValue = useMemo(
    () => ({ showCompletionFeedback }),
    [showCompletionFeedback]
  );

  return (
    <QuestionnaireCompletionFeedbackContext.Provider value={contextValue}>
      {children}
    </QuestionnaireCompletionFeedbackContext.Provider>
  );
};

const useQuestionnaireCompletionFeedback = () =>
  useContext(QuestionnaireCompletionFeedbackContext);

export {
  QuestionnaireCompletionFeedbackProvider,
  useQuestionnaireCompletionFeedback,
};
